import PageHero from "@/components/PageHero";
import { IMG_ABOUT } from "@/lib/constants";
import { Link } from "react-router-dom";
import { ArrowRight, Heart, Scale, Leaf, Users } from "lucide-react";

const values = [
  {
    icon: Heart,
    title: "People First",
    body: "Housing should serve people. Every solution begins with the family, the homeowner, the neighborhood.",
  },
  {
    icon: Scale,
    title: "Responsible Investment",
    body: "Balanced, transparent, and measured. We invest where it strengthens communities — not just balance sheets.",
  },
  {
    icon: Leaf,
    title: "Long-term Strength",
    body: "We measure success in generations. Sustainable housing builds resilient neighborhoods.",
  },
  {
    icon: Users,
    title: "Community Partnership",
    body: "We work alongside local nonprofits, agencies, and leaders to multiply our collective impact.",
  },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero
        testIdPrefix="about-hero"
        eyebrow="About Us"
        title="Founded on a simple belief: every local family deserves a path forward."
        subtitle="Hawaii Housing Foundation exists to provide real solutions that help residents navigate housing challenges while preserving the long-term strength of our communities."
        image={IMG_ABOUT}
      />

      {/* OUR STORY */}
      <section className="bg-brand-ivory py-24 md:py-32" data-testid="about-story">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          <div className="lg:col-span-5">
            <p className="eyebrow text-brand-gold">Our Story</p>
            <h2 className="mt-5 font-heading text-3xl md:text-4xl lg:text-5xl text-brand-ink leading-[1.15]">
              Solutions for today. <br />
              <span className="italic text-brand-forest">Stability for tomorrow.</span>
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-brand-ink/80 text-base md:text-lg leading-relaxed font-light">
            <p>
              We understand that life happens. Job loss, illness, divorce, unexpected expenses,
              rising costs, and financial hardship can place tremendous pressure on homeowners and
              their families. Too often, people feel they have nowhere to turn and no options
              available to them.
            </p>
            <p>
              Our mission is to provide real solutions that help Hawaii residents navigate housing
              challenges while preserving the long-term strength of our communities. Through
              strategic real estate investment, housing advocacy, education, and community
              partnerships, we work to create opportunities that benefit both homeowners and
              neighborhoods.
            </p>
            <p>
              Unlike traditional investment groups focused solely on profit, Hawaii Housing
              Foundation is committed to balancing responsible investment with community impact. We
              believe long-term success is achieved by creating positive outcomes for homeowners,
              neighborhoods, and future generations.
            </p>
          </div>
        </div>
      </section>

      {/* MISSION CARD */}
      <section className="relative bg-white py-24 md:py-32 border-y border-brand-forest/10" data-testid="about-mission">
        <div className="max-w-5xl mx-auto px-6 md:px-10 lg:px-12">
          <div className="border-l-4 border-brand-gold pl-8 md:pl-12">
            <p className="eyebrow text-brand-gold">Mission Statement</p>
            <h2 className="mt-6 font-heading text-2xl md:text-3xl lg:text-4xl text-brand-ink leading-[1.3]">
              "To provide housing solutions that strengthen Hawaii's communities by supporting
              homeowners, creating opportunities, preserving housing stability, and investing with
              purpose."
            </h2>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="bg-brand-ivory py-24 md:py-32 relative overflow-hidden" data-testid="about-values">
        <div className="absolute inset-0 bg-topo opacity-100 pointer-events-none" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
          <div className="max-w-3xl">
            <p className="eyebrow text-brand-gold">Our Values</p>
            <h2 className="mt-5 font-heading text-3xl md:text-4xl lg:text-5xl text-brand-ink leading-tight">
              The principles that guide every decision we make.
            </h2>
          </div>

          <div className="mt-16 grid sm:grid-cols-2 gap-6">
            {values.map((v, i) => {
              const Icon = v.icon;
              return (
                <div
                  key={v.title}
                  data-testid={`value-card-${i}`}
                  className="bg-white border border-brand-forest/10 p-10 rounded-sm hover:border-brand-gold/60 transition-all duration-500"
                >
                  <div className="flex items-start gap-5">
                    <div className="shrink-0 w-12 h-12 flex items-center justify-center bg-brand-forest/5 rounded-sm">
                      <Icon size={22} className="text-brand-forest" />
                    </div>
                    <div>
                      <h3 className="font-heading text-2xl text-brand-ink">{v.title}</h3>
                      <p className="mt-3 text-brand-ink/70 leading-relaxed font-light">{v.body}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* VISION */}
      <section className="bg-brand-forest text-white py-24 md:py-32" data-testid="about-vision">
        <div className="max-w-5xl mx-auto px-6 md:px-10 lg:px-12 text-center">
          <p className="eyebrow text-brand-gold">Our Vision</p>
          <h2 className="mt-6 font-heading text-3xl md:text-4xl lg:text-5xl leading-tight">
            To expand our community impact through housing assistance, education, and partnerships
            that help local families achieve <span className="text-brand-gold italic">greater stability.</span>
          </h2>
          <div className="mt-12">
            <Link
              to="/services"
              data-testid="about-vision-cta"
              className="inline-flex items-center gap-2 px-8 py-4 bg-brand-gold text-brand-ink text-[12px] uppercase tracking-[0.24em] font-semibold rounded-sm hover:bg-white transition-all duration-300"
            >
              See Our Solutions <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
